// The projection: fold the event log into current-state records. Application is
// idempotent (each event id applied at most once) so re-folding a shard tail, or
// seeing our own optimistic write echoed back by the watch, is always safe.
//
// Field conflicts resolve last-writer-wins by (ts, id). Because we apply events
// in log order and appends are monotonic, later events simply overwrite earlier
// ones; on a full rebuild we sort by (ts, id) first to make that deterministic.
//
// Two things are DERIVED here rather than stored:
//   · lineage — from @mentions in each project's `context` field
//   · backlinks — from @mentions in any rich field or entry body
// That's the v2 division of labor: mentions are soft edges (free, unlimited,
// written in prose), and only relationships that carry their own data — a
// a support edge's direction — are stored explicitly.

import type { Actor, CrumEvent } from './events'
import { mentionedIds, mentionsWithContext } from './mentions'
import type { Config, EntryKind, EntityKind, Visibility, WorkStatus } from './model'
import {
  asPriority,
  asVisibility,
  DESCRIPTION_KEYS,
  isBooked,
  isCommitted,
  kindOfId,
  mentionFields,
  showsToClient,
} from './model'

export interface EntityRecord {
  id: string
  kind: EntityKind
  fields: Record<string, string | number>
  tags: string[]
  links: string[]
  archived: boolean
  createdBy: Actor | null
  createdAt: string
  updatedBy: Actor | null
  updatedAt: string
}

/** One timeline item on any subject. Folds v1's `note.*` events too. */
export interface EntryRecord {
  id: string
  /** the record this entry is filed under (any entity id) */
  subject: string
  /** as WRITTEN: entryKind() maps retired keys (`call`) to a current one on read */
  kind: EntryKind | 'call'
  /** 'agent' entries were pushed by an ingest agent, not typed by a human */
  source: 'human' | 'agent'
  /** when it happened — distinct from createdAt, so you can log yesterday's call today */
  occurredAt: string
  body: string
  archived: boolean
  createdBy: Actor | null
  createdAt: string
  updatedBy: Actor | null
  updatedAt: string
}

export interface CommentRecord {
  id: string
  /**
   * What this comment is on: an entry, a board row, anything with an id.
   *
   * It was `entry` and only ever an entry, which is why a board row had nowhere to
   * hold a conversation — the one place a client and the team most need to argue
   * about a specific thing. Widening the target costs nothing: a comment was never
   * about the entry's *shape*, only about which record it hangs from.
   *
   * `entry` is still read for older comments; see `commentsFor`.
   */
  target: string
  entry: string
  body: string
  archived: boolean
  createdBy: Actor | null
  createdAt: string
}

/**
 * A COMMITMENT: something a deal promised, with a date, which can be marked delivered.
 *
 * This is the one thing the retired `resource` record was genuinely for, in the shape it
 * should have had. What made `resource` wrong was conflating three jobs on any record — a
 * link shelf, a due list, a delivered list — so "delivered" ended up describing a URL.
 * The link half is a field on the work now; this is the obligation half, and it hangs off
 * the DEAL because the contract is what creates the obligation.
 *
 * `work` is deliberately optional: you can promise a dataset by September before anything
 * exists, and point it at the work later. A work never carries due/delivered itself — the
 * promise has a status, the thing doesn't.
 */
export interface CommitmentRecord {
  id: string
  deal: string
  label: string
  /** ISO date (yyyy-mm-dd), or '' for "agreed, no date yet" */
  due: string
  /** ISO date once it's done; '' means still owed */
  deliveredAt: string
  /** the work that satisfies this, once it exists. May be '' forever. */
  work: string
  note: string
  archived: boolean
  createdBy: Actor | null
  createdAt: string
  updatedBy: Actor | null
  updatedAt: string
}

// A `LineageRecord` used to live here: an authored connection with its own consent
// setting, one row per edge. It is gone. Connections are read back out of the prose
// (`allConnections`), and consent lives on the two records the edge touches plus the
// org at the far end — see `Connection` below for why that is the same decision said
// once instead of twice.

/** deal ↔ work. Untyped on purpose: the work's own `kind` says what it is. */
export interface SupportEdge {
  deal: string
  project: string
}

export interface StageEntry {
  ts: string
  from: string | null
  to: string
  by: Actor | null
}

/**
 * A dated bucket on one board. **Flat — a group never contains another group.**
 * Nesting was considered and rejected: a flat board stays spreadsheet-shaped,
 * which is what makes it sortable and legible at a glance.
 *
 * It is not a `CommitmentRecord`, though it rhymes with one. A commitment hangs
 * off a DEAL and names what was promised; a group hangs off a board and holds the
 * rows. Overloading commitment's `work` field to mean both "what satisfies this"
 * and "which board this belongs to" is the wrong-axis reuse crum keeps removing.
 *
 * `due` here is the date you PROMISED. An item's `due` is the date you EXPECT —
 * separate fields on purpose, because a red group over green items is the signal.
 */
export interface GroupRecord {
  id: string
  /** the `project` record whose board this sits on */
  work: string
  label: string
  /** ISO date (yyyy-mm-dd), or '' for an undated bucket — a backlog */
  due: string
  /** position on the board; sparse, so a reorder writes one event not N */
  order: number
  visibility: Visibility
  archived: boolean
  createdBy: Actor | null
  createdAt: string
  updatedBy: Actor | null
  updatedAt: string
}

/**
 * A row on a board.
 *
 * `waitingOn` is stored **one direction only**, on the blocked item, and the
 * reverse is derived (see `blocking`). Two-way authoring is where dependency
 * features rot, and a flat board only ever has to render the blocked side.
 *
 * There is deliberately no `blocked` field: blocked-ness is derived from the
 * edges by `isBlocked`. A hand-typed blocked flag goes stale, and a stale one on
 * a client's page is worse than none at all.
 */
export interface ItemRecord {
  id: string
  /**
   * The board this row is on. Added 2026-09-17 so a group can be OPTIONAL: an item
   * used to reach its work only through its group, which meant a board could not
   * hold a row until somebody had invented a bucket to put it in. Most boards start
   * as a flat list and grow buckets later, so requiring the bucket first had the
   * order backwards.
   *
   * Legacy items have '' here and are found through their group instead; see
   * `itemsForWork`.
   */
  work: string
  /** the group this row sits in, or '' for ungrouped. Moving between buckets — or
   *  out of one — is a single field write, so a row keeps its id and its history. */
  group: string
  label: string
  /** team-owned: next / in flight / in review / done / someday */
  status: string
  /** ISO date, or '' */
  due: string
  /** person id, or '' */
  owner: string
  /**
   * The row's own prose, in markdown. Where a row says where it came from — the
   * recording, the timestamp, the frame — which is the provenance chain the whole
   * product is sold on (positioning.md §2) and which had nowhere to live: a board
   * row could carry a label and nothing else.
   */
  note: string
  visibility: Visibility
  /** 0 unset, 1 low, 2 medium, 3 high. The CLIENT owns this column. */
  clientPriority: number
  /**
   * `'client'` when the row was added from the client's seat, `''` when the team
   * added it. **A flag, not a gate.**
   *
   * The alternative was routing a client's row through the staging table the way an
   * agent's suggestion goes. That table exists for one reason — a suggestion must
   * never reach a client — and a client's own row on their own board carries no such
   * hazard: it is already theirs and only their org can see it. Gating it would mean
   * typing a row and watching nothing appear, and would turn every one into an
   * approval, which is the queue-that-gets-rubber-stamped failure the plan names.
   *
   * So the row lands in the log immediately and says where it came from, and the
   * team's board marks it so it gets triaged rather than silently absorbed.
   */
  addedBy: string
  /** ids of items that must land first. One direction; reverse is derived. */
  waitingOn: string[]
  /**
   * Where the row sits in its bucket. Sparse, like a group's, so dragging one row
   * writes ONE event rather than renumbering the whole board.
   *
   * 0 means nobody has said. A board that has never been dragged is therefore all
   * zeroes and falls through to the id tiebreak, which is creation order — what the
   * board has always shown, and now says so rather than being an accident of how the
   * ids sort.
   */
  order: number
  archived: boolean
  createdBy: Actor | null
  createdAt: string
  updatedBy: Actor | null
  updatedAt: string
}

export interface State {
  entities: Record<string, EntityRecord>
  entries: Record<string, EntryRecord>
  comments: Record<string, CommentRecord>
  commitments: Record<string, CommitmentRecord>
  groups: Record<string, GroupRecord>
  items: Record<string, ItemRecord>
  /** keyed `${deal}|${project}` so add/remove folds to a set */
  support: Record<string, SupportEdge>
  dealStages: Record<string, StageEntry[]>
  /**
   * The most recent applied events, in application order, for the activity feed —
   * and ONLY for it. Bounded on purpose: see EVENT_RING.
   */
  events: CrumEvent[]
  applied: Set<string>
}

export function emptyState(): State {
  return {
    entities: {},

    entries: {},
    comments: {},
    commitments: {},
    groups: {},
    items: {},
    support: {},
    dealStages: {},
    events: [],
    applied: new Set(),
  }
}

// ── coercion ─────────────────────────────────────────────────────────────────

function asString(v: unknown): string {
  return typeof v === 'string' ? v : ''
}
function oneOf<T extends string>(v: unknown, allowed: readonly T[], fallback: T): T {
  const s = asString(v)
  return (allowed as readonly string[]).includes(s) ? (s as T) : fallback
}

// `call` is still accepted from older logs: entryKind() maps it to 'meeting' on read,
// so the stored value stays whatever was written and nothing is rewritten.
const ENTRY_KIND_KEYS = ['note', 'call', 'meeting', 'update', 'decision'] as const

// ── dispatch ─────────────────────────────────────────────────────────────────

/**
 * How many events the projection keeps for the activity feed, and the slack it
 * trims at.
 *
 * `state.events` has exactly one reader — the Activity page — and that page shows a
 * window of the most recent changes. Keeping the entire log for it cost twice:
 * every event stayed resident as a parsed object, and `saveCache` re-serialized the
 * whole array into IndexedDB a second after every keystroke. At 100k events that is
 * a ~14MB structured clone per idle second, which is a stutter long before it is a
 * memory problem.
 *
 * So the array is a ring. Nothing else reads it: the projection is folded from the
 * log, the export streams from the server, and `applied` — which is what actually
 * makes a replay idempotent — is untouched by the trim. Trimming with slack keeps
 * the splice amortized: an import of 50k events pays for it fifty times, not
 * 45,000.
 */
const EVENT_RING = 5000
const EVENT_RING_SLACK = 1000

export function applyEvent(state: State, ev: CrumEvent): void {
  if (state.applied.has(ev.id)) return
  state.applied.add(ev.id)
  state.events.push(ev)
  if (state.events.length > EVENT_RING + EVENT_RING_SLACK) {
    state.events.splice(0, state.events.length - EVENT_RING)
  }

  const [entity, verb = ''] = ev.type.split('.')
  const data = ev.data ?? {}

  switch (entity) {
    case 'project':
    case 'artifact':
    case 'deal':
    case 'org':
    case 'person':
      applyEntity(state, ev, verb, data)
      break
    case 'entry':
    // v1 wrote `note.*` with { target, body }. Same record, older vocabulary —
    // fold it as an entry of kind 'note' so no history needs rewriting.
    case 'note':
      applyEntry(state, ev, verb, data, entity === 'note')
      break
    case 'comment':
      applyComment(state, ev, verb, data)
      break
    case 'commitment':
      applyCommitment(state, ev, verb, data)
      break
    case 'group':
      applyGroup(state, ev, verb, data)
      break
    case 'item':
      applyItem(state, ev, verb, data)
      break
    case 'support':
      applySupport(state, ev, verb, data)
      break
    case 'tag':
      applyTag(state, ev, verb, data)
      break
    case 'link':
      applyLink(state, ev, verb, data)
      break
    default:
      // Unknown event type from a newer build: recorded in events (so it still
      // shows in activity), otherwise ignored. Forward-compatible by design.
      break
  }
}

function touch(rec: { updatedAt: string; updatedBy: Actor | null }, ev: CrumEvent): void {
  rec.updatedAt = ev.ts
  rec.updatedBy = ev.actor
}

function fieldsFrom(data: Record<string, unknown>): Record<string, string | number> {
  const out: Record<string, string | number> = {}
  for (const [k, v] of Object.entries(data)) {
    if (typeof v === 'string' || typeof v === 'number') out[k] = v
  }
  return out
}

// ── entities ─────────────────────────────────────────────────────────────────

function applyEntity(
  state: State,
  ev: CrumEvent,
  verb: string,
  data: Record<string, unknown>,
): void {
  const kind = kindOfId(ev.subject)
  if (!kind) return

  if (verb === 'create') {
    if (state.entities[ev.subject]) return
    state.entities[ev.subject] = {
      id: ev.subject,
      kind,
      fields: fieldsFrom(data),
      tags: [],
      links: [],
      archived: false,
      createdBy: ev.actor,
      createdAt: ev.ts,
      updatedBy: ev.actor,
      updatedAt: ev.ts,
    }
    return
  }

  const rec = state.entities[ev.subject]
  if (!rec) return

  if (verb === 'update') {
    Object.assign(rec.fields, fieldsFrom(data))
    touch(rec, ev)
  } else if (verb === 'archive') {
    rec.archived = true
    touch(rec, ev)
  } else if (verb === 'restore') {
    rec.archived = false
    touch(rec, ev)
  } else if (verb === 'stage_change') {
    const to = asString(data.to)
    if (!to) return
    rec.fields.stage = to
    ;(state.dealStages[ev.subject] ??= []).push({
      ts: ev.ts,
      from: data.from == null ? null : asString(data.from),
      to,
      by: ev.actor,
    })
    touch(rec, ev)
  }
}

// ── entries (incl. legacy notes) ─────────────────────────────────────────────

function applyEntry(
  state: State,
  ev: CrumEvent,
  verb: string,
  data: Record<string, unknown>,
  legacy: boolean,
): void {
  if (verb === 'create') {
    if (state.entries[ev.subject]) return
    // v1 called the target `target`; v2 calls it `subject`.
    const subject = asString(data.subject) || asString(data.target)
    state.entries[ev.subject] = {
      id: ev.subject,
      subject,
      kind: legacy ? 'note' : (oneOf(data.kind, ENTRY_KIND_KEYS, 'note') as EntryRecord['kind']),
      source: asString(data.source) === 'agent' ? 'agent' : 'human',
      occurredAt: asString(data.occurredAt) || ev.ts,
      body: asString(data.body),
      archived: false,
      createdBy: ev.actor,
      createdAt: ev.ts,
      updatedBy: ev.actor,
      updatedAt: ev.ts,
    }
    return
  }
  const rec = state.entries[ev.subject]
  if (!rec) return
  if (verb === 'update') {
    if ('body' in data) rec.body = asString(data.body)
    if ('kind' in data)
      rec.kind = oneOf(data.kind, ENTRY_KIND_KEYS, rec.kind) as EntryRecord['kind']
    if ('occurredAt' in data) rec.occurredAt = asString(data.occurredAt) || rec.occurredAt
    touch(rec, ev)
  } else if (verb === 'archive') {
    rec.archived = true
    touch(rec, ev)
  } else if (verb === 'restore') {
    rec.archived = false
    touch(rec, ev)
  }
}

function applyComment(
  state: State,
  ev: CrumEvent,
  verb: string,
  data: Record<string, unknown>,
): void {
  if (verb === 'create') {
    if (state.comments[ev.subject]) return
    state.comments[ev.subject] = {
      id: ev.subject,
      // v1 keyed comments by `note`; v2 by `entry`; v3 by `target`, which may be
      // any record. Each reader falls back to the one before it.
      target: asString(data.target) || asString(data.entry) || asString(data.note),
      entry: asString(data.entry) || asString(data.note),
      body: asString(data.body),
      archived: false,
      createdBy: ev.actor,
      createdAt: ev.ts,
    }
    return
  }
  const rec = state.comments[ev.subject]
  if (!rec) return
  if (verb === 'update') {
    if ('body' in data) rec.body = asString(data.body)
  } else if (verb === 'archive') {
    rec.archived = true
  } else if (verb === 'restore') {
    rec.archived = false
  }
}

function applyCommitment(
  state: State,
  ev: CrumEvent,
  verb: string,
  data: Record<string, unknown>,
): void {
  if (verb === 'create') {
    if (state.commitments[ev.subject]) return
    state.commitments[ev.subject] = {
      id: ev.subject,
      deal: asString(data.deal),
      label: asString(data.label),
      due: asString(data.due),
      deliveredAt: asString(data.deliveredAt),
      work: asString(data.work),
      note: asString(data.note),
      archived: false,
      createdBy: ev.actor,
      createdAt: ev.ts,
      updatedBy: ev.actor,
      updatedAt: ev.ts,
    }
    return
  }
  const rec = state.commitments[ev.subject]
  if (!rec) return
  if (verb === 'update') {
    for (const key of ['deal', 'label', 'due', 'deliveredAt', 'work', 'note'] as const) {
      if (key in data) rec[key] = asString(data[key])
    }
    touch(rec, ev)
  } else if (verb === 'archive') {
    rec.archived = true
    touch(rec, ev)
  } else if (verb === 'restore') {
    rec.archived = false
    touch(rec, ev)
  }
}

function asNumber(v: unknown, fallback = 0): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : fallback
}

/** String ids, deduped and order-preserving — the shape every edge list takes. */
function asIdList(v: unknown): string[] {
  if (!Array.isArray(v)) return []
  const out: string[] = []
  for (const x of v) if (typeof x === 'string' && x && !out.includes(x)) out.push(x)
  return out
}

function applyGroup(
  state: State,
  ev: CrumEvent,
  verb: string,
  data: Record<string, unknown>,
): void {
  if (verb === 'create') {
    if (state.groups[ev.subject]) return
    state.groups[ev.subject] = {
      id: ev.subject,
      work: asString(data.work),
      label: asString(data.label),
      due: asString(data.due),
      order: asNumber(data.order),
      visibility: asVisibility(data.visibility),
      archived: false,
      createdBy: ev.actor,
      createdAt: ev.ts,
      updatedBy: ev.actor,
      updatedAt: ev.ts,
    }
    return
  }
  const rec = state.groups[ev.subject]
  if (!rec) return
  if (verb === 'update') {
    for (const key of ['work', 'label', 'due'] as const) {
      if (key in data) rec[key] = asString(data[key])
    }
    if ('order' in data) rec.order = asNumber(data.order, rec.order)
    if ('visibility' in data) rec.visibility = asVisibility(data.visibility)
    touch(rec, ev)
  } else if (verb === 'archive') {
    rec.archived = true
    touch(rec, ev)
  } else if (verb === 'restore') {
    rec.archived = false
    touch(rec, ev)
  }
}

function applyItem(state: State, ev: CrumEvent, verb: string, data: Record<string, unknown>): void {
  if (verb === 'create') {
    if (state.items[ev.subject]) return
    state.items[ev.subject] = {
      id: ev.subject,
      work: asString(data.work),
      group: asString(data.group),
      label: asString(data.label),
      status: asString(data.status) || 'next',
      due: asString(data.due),
      owner: asString(data.owner),
      note: asString(data.note),
      visibility: asVisibility(data.visibility),
      clientPriority: asPriority(data.clientPriority),
      addedBy: asString(data.addedBy) === 'client' ? 'client' : '',
      waitingOn: asIdList(data.waitingOn),
      order: asNumber(data.order),
      archived: false,
      createdBy: ev.actor,
      createdAt: ev.ts,
      updatedBy: ev.actor,
      updatedAt: ev.ts,
    }
    return
  }
  const rec = state.items[ev.subject]
  if (!rec) return
  if (verb === 'update') {
    // `group` is here so moving a row between buckets is one field write rather
    // than a delete and a re-create — the row keeps its id, its comments and its
    // history when it moves.
    for (const key of ['work', 'group', 'label', 'status', 'due', 'owner', 'note'] as const) {
      if (key in data) rec[key] = asString(data[key])
    }
    if ('visibility' in data) rec.visibility = asVisibility(data.visibility)
    if ('clientPriority' in data) rec.clientPriority = asPriority(data.clientPriority)
    if ('waitingOn' in data) rec.waitingOn = asIdList(data.waitingOn)
    if ('order' in data) rec.order = asNumber(data.order, rec.order)
    touch(rec, ev)
  } else if (verb === 'block') {
    // A dedicated verb so "X now waits on Y" is one small event rather than a
    // whole-array write two clients can clobber.
    const add = asString(data.on)
    if (add && add !== rec.id && !rec.waitingOn.includes(add)) rec.waitingOn.push(add)
    touch(rec, ev)
  } else if (verb === 'unblock') {
    const drop = asString(data.on)
    rec.waitingOn = rec.waitingOn.filter((x) => x !== drop)
    touch(rec, ev)
  } else if (verb === 'archive') {
    rec.archived = true
    touch(rec, ev)
  } else if (verb === 'restore') {
    rec.archived = false
    touch(rec, ev)
  }
}

function applySupport(
  state: State,
  ev: CrumEvent,
  verb: string,
  data: Record<string, unknown>,
): void {
  const project = asString(data.project)
  if (!project) return
  // Old events carry a `role` (supports / produces) in `data`. It's ignored, and the
  // key no longer includes it, so a work attached under both roles folds to one edge
  // and a `support.remove` written before this change still removes it.
  const key = `${ev.subject}|${project}`
  if (verb === 'add') {
    state.support[key] = { deal: ev.subject, project }
  } else if (verb === 'remove') {
    delete state.support[key]
  }
}

// ── tags & links ─────────────────────────────────────────────────────────────

function applyTag(state: State, ev: CrumEvent, verb: string, data: Record<string, unknown>): void {
  const rec = state.entities[ev.subject]
  if (!rec) return
  const label = asString(data.label).trim()
  if (!label) return
  if (verb === 'add') {
    if (!rec.tags.includes(label)) {
      rec.tags.push(label)
      rec.tags.sort()
    }
  } else if (verb === 'remove') {
    rec.tags = rec.tags.filter((t) => t !== label)
  }
}

function applyLink(state: State, ev: CrumEvent, verb: string, data: Record<string, unknown>): void {
  const from = state.entities[ev.subject]
  const toId = asString(data.to)
  const to = state.entities[toId]
  if (!from || !to) return
  if (verb === 'add') {
    if (!from.links.includes(toId)) from.links.push(toId)
    if (!to.links.includes(from.id)) to.links.push(from.id)
  } else if (verb === 'remove') {
    from.links = from.links.filter((l) => l !== toId)
    to.links = to.links.filter((l) => l !== from.id)
  }
}

// ── selectors: entities ──────────────────────────────────────────────────────

const newestFirst = (a: { updatedAt: string }, b: { updatedAt: string }) =>
  b.updatedAt < a.updatedAt ? -1 : 1

export function entitiesOfKind(
  state: State,
  kind: EntityKind,
  includeArchived = false,
): EntityRecord[] {
  return Object.values(state.entities)
    .filter((e) => e.kind === kind && (includeArchived || !e.archived))
    .sort(newestFirst)
}

export function fieldStr(rec: EntityRecord | undefined, key: string): string {
  const v = rec?.fields[key]
  return typeof v === 'string' ? v : typeof v === 'number' ? String(v) : ''
}

/**
 * A record's prose, whatever key it was written under: `description` today, and the
 * `context` / `summary` / `about` fields it replaced in older logs.
 */
export function descriptionOf(rec: EntityRecord | undefined): string {
  for (const key of DESCRIPTION_KEYS) {
    const v = fieldStr(rec, key)
    if (v.trim()) return v
  }
  return ''
}

export function titleOf(state: State, id: string): string {
  const rec = state.entities[id]
  if (!rec) return ''
  // titleField without importing KINDS twice: name for everything but deals.
  return fieldStr(rec, rec.kind === 'deal' ? 'title' : 'name')
}

/** True when a record is set aside but not archived — hatched, out of rollups. */
/**
 * Has this work been handed over?
 *
 * The one axis that separates a **project** from an **artifact**, and they are the
 * same record type seen at two points in its life:
 *
 *   · a project is work still going — including an ongoing hosting or support
 *     arrangement, which simply never gets an end date and so is a project
 *     forever. That is the right answer: you are still doing it.
 *   · an artifact is work that landed — a demo, a document, a report. A thing
 *     that exists.
 *
 * So "is this an artifact or a project?" is not a field anyone sets, and nothing
 * has to be filed twice. An end date in the past is the whole rule.
 */
export function isDelivered(rec: EntityRecord | undefined, today: string): boolean {
  if (!rec) return false
  const end = fieldStr(rec, 'end')
  return !!end && end <= today
}

export function linkedOfKind(
  state: State,
  entityId: string,
  kind: EntityKind,
  includeArchived = false,
): EntityRecord[] {
  const rec = state.entities[entityId]
  if (!rec) return []
  return rec.links
    .map((id) => state.entities[id])
    .filter((e): e is EntityRecord => !!e && e.kind === kind && (includeArchived || !e.archived))
    .sort(newestFirst)
}

// ── selectors: entries & comments ────────────────────────────────────────────

const byOccurredDesc = (a: EntryRecord, b: EntryRecord) =>
  a.occurredAt < b.occurredAt
    ? 1
    : a.occurredAt > b.occurredAt
      ? -1
      : b.createdAt < a.createdAt
        ? -1
        : 1

/** Entries filed directly on a subject. */
export function entriesFor(
  state: State,
  subjectId: string,
  includeArchived = false,
): EntryRecord[] {
  return Object.values(state.entries)
    .filter((e) => e.subject === subjectId && (includeArchived || !e.archived))
    .sort(byOccurredDesc)
}

/** Entries filed elsewhere that @mention this record — the other half of the
 *  timeline. One call logged against a demo shows up on the org it mentions. */
export function entriesMentioning(state: State, id: string): EntryRecord[] {
  return Object.values(state.entries)
    .filter((e) => !e.archived && e.subject !== id && mentionedIds(e.body).includes(id))
    .sort(byOccurredDesc)
}

/** The merged view: filed here, plus mentioned from anywhere. */
export function timelineFor(state: State, id: string): EntryRecord[] {
  return [...entriesFor(state, id), ...entriesMentioning(state, id)].sort(byOccurredDesc)
}

export function commentsForEntry(state: State, entryId: string): CommentRecord[] {
  return commentsFor(state, entryId)
}

/** Every comment on a record, oldest first — a thread reads down the page. */
export function commentsFor(
  state: State,
  targetId: string,
  includeArchived = false,
): CommentRecord[] {
  return Object.values(state.comments)
    .filter(
      (c) => (c.target === targetId || c.entry === targetId) && (includeArchived || !c.archived),
    )
    .sort((a, b) => (a.createdAt < b.createdAt ? -1 : 1))
}

export function allEntries(state: State): EntryRecord[] {
  return Object.values(state.entries)
    .filter((e) => !e.archived)
    .sort(byOccurredDesc)
}

/**
 * Two date strings live side by side in the log: an entry's `occurredAt` may be
 * date-only (yyyy-mm-dd, because you pick a day), while `updatedAt` is a full
 * timestamp. Comparing them as raw strings is wrong — '2026-08-04' sorts BEFORE
 * '2026-08-04T09:00:00Z' even though they're the same day — so normalize to
 * midday before comparing.
 */
function comparable(iso: string): string {
  return iso.length <= 10 && iso ? `${iso}T12:00:00.000Z` : iso
}

/**
 * When this record was last touched by anyone — the staleness signal behind the
 * Loose ends page. Counts both contact (the newest entry on or mentioning it) and
 * work on the record itself, so a deal created today never reads as neglected.
 * Returns the winning value as-written, for display.
 */
export function lastTouch(state: State, id: string): string {
  const entry = timelineFor(state, id)[0]?.occurredAt ?? ''
  const updated = state.entities[id]?.updatedAt ?? ''
  return comparable(entry) > comparable(updated) ? entry : updated
}

// ── selectors: boards, groups, items ─────────────────────────────────────────

/** A `project` record is a board iff something has put a group on it. There is no
 *  "is a board" flag: having groups is what makes it one. */
export function isBoard(state: State, workId: string): boolean {
  return Object.values(state.groups).some((g) => g.work === workId && !g.archived)
}

/** Groups on a board, in board order. Undated buckets sort last among equals,
 *  which puts a backlog at the bottom without needing a special case. */
export function groupsForWork(state: State, workId: string): GroupRecord[] {
  return Object.values(state.groups)
    .filter((g) => g.work === workId && !g.archived)
    .sort((a, b) => a.order - b.order || a.label.localeCompare(b.label))
}

/**
 * Board order: the rows somebody arranged, then everything else in creation order.
 *
 * `order` 0 means UNSET, and unset sorts LAST rather than first. That is the whole
 * subtlety, and it is the difference between the two things a board must get right:
 * a bucket nobody has touched is every row at 0, so it falls through to the id
 * tiebreak — and a record id is a ULID, so that IS creation order, which is what the
 * board has always shown. Add a row to a bucket you HAVE arranged and it lands at the
 * bottom, where a new row belongs, rather than jumping above work you deliberately
 * put first.
 *
 * The id tiebreak was the only order a board had before this. It was real and it was
 * invisible, which is why nobody could explain it.
 */
export function byBoardOrder(a: ItemRecord, b: ItemRecord): number {
  const ao = a.order ?? 0
  const bo = b.order ?? 0
  if (ao !== bo && (ao === 0 || bo === 0)) return ao === 0 ? 1 : -1
  return ao - bo || a.id.localeCompare(b.id)
}

/** The gap a board leaves between rows, so most drops land between two numbers
 *  without disturbing either. Same idea as a group's `order`. */
export const ORDER_STEP = 10

/**
 * Where a dragged row should land, as the fewest field writes that will hold it.
 *
 * `rows` is the bucket in the order it is currently DRAWN, `id` is the row being
 * moved and `at` is the index it was dropped at within that drawing. The answer is
 * usually one update: the midpoint between the two rows it landed between.
 *
 * It renumbers the whole bucket only when it has to — when the rows are all still at
 * 0 (nobody has ever dragged here), or when two neighbours have closed the gap
 * between them. That keeps the common act one event, which is the same reason a
 * group's order is sparse, while never leaving the board in an order it cannot
 * express.
 */
export function reorderItems(
  rows: ItemRecord[],
  moving: ItemRecord,
  at: number,
): Array<{ id: string; order: number }> {
  // `rows` is the destination bucket exactly as it is drawn now. It contains `moving`
  // when the row is being nudged within its own bucket, and does not when it is
  // arriving from another one — which is also the difference between a drop that can
  // be a no-op and one that never is.
  const wasAt = rows.findIndex((r) => r.id === moving.id)
  const without = rows.filter((r) => r.id !== moving.id)
  const index = Math.max(0, Math.min(at, without.length))
  // Dropped back where it already was.
  if (wasAt >= 0 && wasAt === index) return []
  const before = without[index - 1]
  const after = without[index]

  const lo = before ? (before.order ?? 0) : 0
  const hi = after && (after.order ?? 0) > 0 ? after.order : lo + ORDER_STEP * 2
  // Room to land between them, and both sides actually numbered. Dropping next to an
  // unset row means the bucket has never been arranged, which is the renumber below.
  const roomy = hi - lo >= 2 && (!before || lo > 0) && (!after || (after.order ?? 0) > 0)
  if (roomy) return [{ id: moving.id, order: Math.floor((lo + hi) / 2) }]

  // No room, or an untouched bucket: lay the whole thing out and write only the rows
  // whose number actually changes.
  const next = [...without.slice(0, index), moving, ...without.slice(index)]
  return next
    .map((r, i) => ({ id: r.id, order: (i + 1) * ORDER_STEP }))
    .filter((r) => {
      const was = rows.find((x) => x.id === r.id)
      return (was?.order ?? 0) !== r.order
    })
}

export function itemsForGroup(state: State, groupId: string): ItemRecord[] {
  return Object.values(state.items)
    .filter((i) => i.group === groupId && !i.archived)
    .sort(byBoardOrder)
}

/** Every item on a board, across its groups, in board order. */
/**
 * Every row on a board, grouped or not.
 *
 * Two paths on purpose: items created since `work` landed carry it directly, and
 * older ones are still reached through their group. Reading both means no migration
 * and no moment where a board looks empty because its rows predate the field.
 */
export function itemsForWork(state: State, workId: string): ItemRecord[] {
  const groupIds = groupIdsOfWork(state, workId)
  return Object.values(state.items)
    .filter((i) => !i.archived && (i.work === workId || (!!i.group && groupIds.has(i.group))))
    .sort(byBoardOrder)
}

/**
 * Every bucket this board has ever had, ARCHIVED INCLUDED — for deciding what is
 * on the board, never for drawing it.
 *
 * A legacy row reaches its board only through its group, so reading live groups
 * here meant archiving a bucket took those rows off the board entirely rather than
 * dropping them to Ungrouped. Membership and display are different questions and
 * this is the membership one.
 */
function groupIdsOfWork(state: State, workId: string): Set<string> {
  const out = new Set<string>()
  for (const g of Object.values(state.groups)) if (g.work === workId) out.add(g.id)
  return out
}

/** Rows on a board that are in no bucket. The list a board starts as. */
export function ungroupedItems(state: State, workId: string): ItemRecord[] {
  const groupIds = new Set(groupsForWork(state, workId).map((g) => g.id))
  return itemsForWork(state, workId)
    .filter((i) => !i.group || !groupIds.has(i.group))
    .sort(byBoardOrder)
}

/**
 * What has been archived off a board: its buckets and its rows.
 *
 * **Archiving a bucket does not take its rows with it, and does not move them.**
 * An item still names the group it was in; `ungroupedItems` collects rows whose
 * group is no longer among the live ones, so they surface under Ungrouped, and
 * restoring the bucket re-collects exactly the rows it had. Moving them on archive
 * would have been a write per row and would have lost which bucket they were in,
 * which is the thing a restore is for.
 */
export function archivedOnBoard(
  state: State,
  workId: string,
): { groups: GroupRecord[]; items: ItemRecord[] } {
  const own = Object.values(state.groups).filter((g) => g.work === workId)
  const ids = new Set(own.map((g) => g.id))
  return {
    groups: own
      .filter((g) => g.archived)
      .sort((a, b) => a.order - b.order || a.label.localeCompare(b.label)),
    items: Object.values(state.items)
      .filter((i) => i.archived && (i.work === workId || (!!i.group && ids.has(i.group))))
      .sort((a, b) => a.id.localeCompare(b.id)),
  }
}

/**
 * What this item is waiting on that has not landed yet.
 *
 * A blocker that is archived, missing, or already done does not block — so an
 * item unblocks itself the moment its blocker is finished, with no second event
 * and nothing to remember to clear.
 */
export function blockersOf(state: State, itemId: string): ItemRecord[] {
  const rec = state.items[itemId]
  if (!rec) return []
  return rec.waitingOn
    .map((id) => state.items[id])
    .filter((b): b is ItemRecord => !!b && !b.archived && b.status !== 'done')
}

/** **Derived, never stored.** See the note on ItemRecord. */
export function isBlocked(state: State, itemId: string): boolean {
  return blockersOf(state, itemId).length > 0
}

/** The reverse edge, computed: which items are waiting on this one. Nobody
 *  authors this side — that is what keeps the two directions from drifting. */
export function blocking(state: State, itemId: string): ItemRecord[] {
  return Object.values(state.items)
    .filter((i) => !i.archived && i.waitingOn.includes(itemId))
    .sort((a, b) => a.id.localeCompare(b.id))
}

/**
 * Would adding `waitingOn` create a cycle? A board where A waits on B waits on A
 * renders two permanently blocked rows and no way to see why, so the edge is
 * refused at the point of authoring rather than untangled later.
 */
export function wouldCycle(state: State, itemId: string, blockerId: string): boolean {
  if (itemId === blockerId) return true
  const seen = new Set<string>()
  const walk = (id: string): boolean => {
    if (id === itemId) return true
    if (seen.has(id)) return false
    seen.add(id)
    return (state.items[id]?.waitingOn ?? []).some(walk)
  }
  return walk(blockerId)
}

// ── selectors: the client's seat ─────────────────────────────────────────────

/**
 * The same board, reduced to what the client may see.
 *
 * This is the whole client view: one filter over the records the team already
 * keeps. Nothing is re-authored, and there is no second copy to go stale.
 *
 * A group survives if it is visible AND has something visible in it — an empty
 * bucket on their page advertises that things were withheld, which is exactly
 * what we are not doing.
 */
/**
 * The projects a client may see: theirs, and promoted.
 *
 * `projectsForOrg` is the team's selector and says nothing about visibility — it
 * answers "whose is this", not "may they see it". Both the client's payload and the
 * client's writes need the second question, and asking it in two places is how they
 * come to disagree.
 */
export function clientProjects(state: State, orgId: string): EntityRecord[] {
  return projectsForOrg(state, orgId).filter((p) =>
    showsToClient(asVisibility(p.fields.visibility)),
  )
}

export function clientBoard(
  state: State,
  workId: string,
): Array<{ group: GroupRecord; items: ItemRecord[] }> {
  const out: Array<{ group: GroupRecord; items: ItemRecord[] }> = []
  for (const group of groupsForWork(state, workId)) {
    if (!showsToClient(group.visibility)) continue
    const items = itemsForGroup(state, group.id).filter((i) => showsToClient(i.visibility))
    if (items.length > 0) out.push({ group, items })
  }
  return out
}

/**
 * Counted for the team's own toolbar ("9 of 12 shown"). Never sent outward: a
 * client is not shown a count of what they cannot see.
 *
 * Counts what `clientBoard` would actually render, which means an item in an
 * internal group does NOT count as shown however its own flag reads. Counting the
 * item alone made the toolbar promise more than the client's page delivered.
 */
export function shownCount(state: State, workId: string): { shown: number; total: number } {
  const groups = groupsForWork(state, workId)
  let shown = 0
  let total = 0
  for (const g of groups) {
    const items = itemsForGroup(state, g.id)
    total += items.length
    if (!showsToClient(g.visibility)) continue
    shown += items.filter((i) => showsToClient(i.visibility)).length
  }
  return { shown, total }
}

/** Every dated group across a client's boards, soonest first — the "coming up"
 *  strip. Group dates only: item dates here would make the client page a second
 *  board, which is the thing to avoid. */
export function milestonesForWorks(
  state: State,
  workIds: string[],
  opts: { clientOnly?: boolean } = {},
): GroupRecord[] {
  const set = new Set(workIds)
  return Object.values(state.groups)
    .filter((g) => set.has(g.work) && !g.archived && g.due)
    .filter((g) => !opts.clientOnly || showsToClient(g.visibility))
    .sort((a, b) => a.due.localeCompare(b.due))
}

// ── selectors: commitments ───────────────────────────────────────────────────

/** Everything a deal promised, soonest first, undated last. */
export function commitmentsForDeal(
  state: State,
  dealId: string,
  includeArchived = false,
): CommitmentRecord[] {
  return Object.values(state.commitments)
    .filter((c) => c.deal === dealId && (includeArchived || !c.archived))
    .sort(byDue)
}

/** Which promises point at this work — read-only on the work's page. */
export function commitmentsForWork(state: State, workId: string): CommitmentRecord[] {
  return Object.values(state.commitments)
    .filter((c) => !c.archived && c.work === workId)
    .sort(byDue)
}

/** Everything still owed, anywhere, soonest first. The overview's one forward look. */
export function owedItems(state: State): CommitmentRecord[] {
  return Object.values(state.commitments)
    .filter((c) => !c.archived && !c.deliveredAt && c.due)
    .sort(byDue)
}

/** Delivered vs owed on one deal, and the next thing due. */
export interface Delivery {
  items: CommitmentRecord[]
  delivered: number
  owed: number
  next: CommitmentRecord | undefined
}

export function deliveryOf(state: State, dealId: string): Delivery {
  const items = commitmentsForDeal(state, dealId)
  const owed = items.filter((c) => !c.deliveredAt)
  return {
    items,
    delivered: items.length - owed.length,
    owed: owed.length,
    // Soonest DATED thing still owed: an undated promise isn't "next".
    next: owed.find((c) => !!c.due),
  }
}

/** Undated sorts last, then by date, then by label so the order is stable. */
function byDue(a: CommitmentRecord, b: CommitmentRecord): number {
  if (!a.due !== !b.due) return a.due ? -1 : 1
  return a.due.localeCompare(b.due) || a.label.localeCompare(b.label)
}

// ── selectors: orgs on a deal ────────────────────────────────────────────────

/**
 * Every org on a deal. The UI writes LINKS (one multi-select control, because a joint
 * deal has several parties), and the legacy single `org` field is still read so older
 * logs keep their counterparty. First one listed is the legacy signer if there is one.
 */
export function orgsForDeal(state: State, dealId: string): EntityRecord[] {
  const legacy = state.entities[fieldStr(state.entities[dealId], 'org')]
  const linked = linkedOfKind(state, dealId, 'org').filter((o) => o.id !== legacy?.id)
  return [...(legacy && legacy.kind === 'org' && !legacy.archived ? [legacy] : []), ...linked]
}

/** Deals an org is on, whether by link or by the legacy `org` field. */
export function dealsForOrg(state: State, orgId: string, includeArchived = false): EntityRecord[] {
  const linked = new Set(state.entities[orgId]?.links ?? [])
  return Object.values(state.entities)
    .filter(
      (d) =>
        d.kind === 'deal' &&
        (includeArchived || !d.archived) &&
        (fieldStr(d, 'org') === orgId || linked.has(d.id)),
    )
    .sort(newestFirst)
}

/**
 * Artifacts an org's projects produced.
 *
 * An artifact links to the project it came out of, not to the org — one hop, so
 * this walks it. Tying artifacts straight to an org would have said the wrong
 * thing: a thing exists because a piece of work made it, and the client is who
 * that work was for.
 */
export function artifactsForOrg(state: State, orgId: string): EntityRecord[] {
  const projects = new Set(projectsForOrg(state, orgId).map((p) => p.id))
  return Object.values(state.entities)
    .filter(
      (a) =>
        a.kind === 'artifact' &&
        !a.archived &&
        // Either it came out of one of this client's projects, or it is simply
        // theirs. The second case is real: something delivered before anyone named
        // the effort around it has no parent project to point at, and inventing one
        // to satisfy the model would be a worse record.
        (a.links.some((id) => projects.has(id)) || a.links.includes(orgId)),
    )
    .sort(newestFirst)
}

/**
 * Whose an artifact is, and what made it — the same two hops `artifactsForOrg`
 * walks, read the other way round, so a list of artifacts can say who each one
 * was for without every caller re-deriving it.
 */
export function orgsForArtifact(state: State, artifactId: string): EntityRecord[] {
  const seen = new Map<string, EntityRecord>()
  for (const org of linkedOfKind(state, artifactId, 'org')) seen.set(org.id, org)
  for (const project of linkedOfKind(state, artifactId, 'project'))
    for (const org of orgsForProject(state, project.id)) seen.set(org.id, org)
  return [...seen.values()]
}

/**
 * Work that belongs to an org: what its deals pay for, plus anything linked to it
 * directly. The direct half is what lets internal work (KF's own projects) and
 * pre-contract work sit on a client page at all — before it, a project with no deal
 * existed in the Projects list and nowhere else.
 */
export function projectsForOrg(state: State, orgId: string): EntityRecord[] {
  const ids = new Set(
    dealsForOrg(state, orgId).flatMap((d) => supportFor(state, d.id).map((s) => s.project)),
  )
  for (const p of linkedOfKind(state, orgId, 'project')) ids.add(p.id)
  return [...ids]
    .map((id) => state.entities[id])
    .filter((p): p is EntityRecord => !!p && p.kind === 'project' && !p.archived)
    .sort(newestFirst)
}

/** Every org marked as us. Normally one; the model does not insist. */
export function internalOrgs(state: State): EntityRecord[] {
  return entitiesOfKind(state, 'org').filter((o) => fieldStr(o, 'state') === 'internal')
}

/**
 * Who can own a row on this project's board: our side, plus their side.
 *
 * The owner control offered every person in the log, which on a real deployment is
 * a list of everyone at every client — so the one name you wanted was buried among
 * people who could not possibly own the row. Scoped to the people linked to this
 * project's client and the people at whichever org is marked `internal`.
 *
 * Falls back to everyone when that comes out empty, because a control that offers
 * nothing is worse than one that offers too much — and it will be empty on a fresh
 * deployment, before anyone has been told which org is us.
 */
export function peopleForProject(state: State, projectId: string): EntityRecord[] {
  const seen = new Map<string, EntityRecord>()
  for (const org of [...orgsForProject(state, projectId), ...internalOrgs(state)])
    for (const person of linkedOfKind(state, org.id, 'person')) seen.set(person.id, person)
  const scoped = [...seen.values()]
  return (scoped.length > 0 ? scoped : entitiesOfKind(state, 'person')).sort((a, b) =>
    fieldStr(a, 'name').localeCompare(fieldStr(b, 'name')),
  )
}

// ── selectors: connections and consent ───────────────────────────────────────

/**
 * A connection, read out of the prose rather than authored as its own record.
 *
 * **A mention in a description is an upstream claim.** Writing "Grew out of
 * @[Union catalogue](prj_x)" on a project says that project came out of the union
 * catalogue, so the edge runs `prj_x → this`. The direction is fixed by which record
 * the sentence is written on, which is why the field's help text asks what this work
 * builds on rather than what it feeds. The other direction is this one reversed.
 *
 * **Visibility is not on the edge.** It is on the two records the edge touches, and a
 * connection is only as visible as the less visible of them. The earlier model put a
 * consent setting on every connection, which meant saying the same thing once per
 * edge and getting it wrong on the one you forgot: a project is either something you
 * can talk about or it is not, and that is a fact about the project.
 */
export interface Connection {
  /** derivable, not stored: one connection per ordered pair */
  id: string
  /** upstream — the work that was drawn on */
  from: string
  /** downstream — the work whose description says so */
  to: string
  /** the sentence that says it */
  label: string
  /** the weaker of the two ends */
  visibility: Visibility
}

/** A record's own answer, defaulting closed: nothing travels by being forgotten. */
export function visibilityOf(rec: EntityRecord | undefined): Visibility {
  const v = fieldStr(rec, 'visibility')
  return v === 'client' || v === 'acknowledgeable' ? v : 'internal'
}

const RANK: Record<Visibility, number> = { internal: 0, client: 1, acknowledgeable: 2 }

/** An edge cannot be more visible than either thing it joins. */
export function weakerVisibility(a: Visibility, b: Visibility): Visibility {
  return RANK[a] <= RANK[b] ? a : b
}

/** Only these two kinds are nodes in the web. A description mentioning a person or a
 *  deal is prose, not ancestry. */
function isWork(rec: EntityRecord | undefined): boolean {
  return !!rec && !rec.archived && (rec.kind === 'project' || rec.kind === 'artifact')
}

/** Every connection in the log, derived. Built in one pass because both directions
 *  need the same scan. */
export function allConnections(state: State): Connection[] {
  const out = new Map<string, Connection>()
  for (const rec of Object.values(state.entities)) {
    if (!isWork(rec)) continue
    const mine = visibilityOf(rec)
    for (const m of mentionsWithContext(descriptionOf(rec))) {
      const up = state.entities[m.id]
      if (m.id === rec.id || !isWork(up)) continue
      const id = `${m.id}->${rec.id}`
      if (out.has(id)) continue
      out.set(id, {
        id,
        from: m.id,
        to: rec.id,
        label: m.sentence,
        visibility: weakerVisibility(visibilityOf(up), mine),
      })
    }
  }
  return [...out.values()]
}

/** Every connection touching a record, either end. */
export function connectionsFor(state: State, id: string): Connection[] {
  return allConnections(state).filter((c) => c.from === id || c.to === id)
}

/** Connections touching anything this client owns — their whole web. */
export function connectionsForOrg(state: State, orgId: string): Connection[] {
  const mine = new Set<string>()
  for (const w of projectsForOrg(state, orgId)) mine.add(w.id)
  for (const a of artifactsForOrg(state, orgId)) mine.add(a.id)
  return allConnections(state)
    .filter((c) => mine.has(c.from) || mine.has(c.to))
    .sort((a, b) => a.label.localeCompare(b.label))
}

/** Which org a record belongs to, for deciding whose consent governs it. */
export function orgOfRecord(state: State, id: string): EntityRecord | undefined {
  const rec = state.entities[id]
  if (!rec) return undefined
  if (rec.kind === 'artifact') return orgsForArtifact(state, id)[0]
  if (rec.kind === 'project') return orgsForProject(state, id)[0]
  if (rec.kind === 'org') return rec
  return undefined
}

/** Has this org agreed to be named on someone else's page? */
export function namingConsent(org: EntityRecord | undefined): string {
  return fieldStr(org, 'naming') || 'none'
}

/**
 * How one end of a connection renders on ANOTHER org's page.
 *
 * Two gates, deliberately both required, and both of them are now facts about
 * something real rather than about an edge. The team marks the WORK as something
 * that may be named; the org at the far end decides it may be named at all. Either
 * one missing and the node is still drawn — as an unnamed one — because dropping it
 * would shrink the web and misrepresent its shape, which is the thing the ecosystem
 * view exists to show (client-surface.md §6).
 */
export type NamedAs = 'named' | 'anonymous' | 'hidden'

export function namedOnOthersPage(state: State, farEnd: string): NamedAs {
  const vis = visibilityOf(state.entities[farEnd])
  if (vis === 'internal') return 'hidden'
  if (vis !== 'acknowledgeable') return 'anonymous'
  return namingConsent(orgOfRecord(state, farEnd)) === 'approved' ? 'named' : 'anonymous'
}

// ── selectors: the state of an engagement ────────────────────────────────────

/**
 * When we last heard from, or spoke to, a client.
 *
 * Deliberately wider than `entriesFor(org)`: a call logged against the deal or the
 * project it was about is still contact with that client, and filing it on the
 * narrower record is the normal thing to do. Looking only at the org's own entries
 * made an actively-running engagement read as silent.
 *
 * Returns the entry's `occurredAt` as written, or '' when there has never been any.
 */
export function lastContact(state: State, orgId: string): string {
  const subjects = new Set<string>([orgId])
  for (const d of dealsForOrg(state, orgId, true)) subjects.add(d.id)
  for (const w of projectsForOrg(state, orgId)) subjects.add(w.id)

  let best = ''
  for (const e of Object.values(state.entries)) {
    if (e.archived || !subjects.has(e.subject)) continue
    if (comparable(e.occurredAt) > comparable(best)) best = e.occurredAt
  }
  return best
}

/**
 * Everything the client page's fact strip needs, in one pass.
 *
 * The point of the strip is that it renders at zero. A brand new client shows no
 * money, no milestone, no open items and no contact, and that is still an answer —
 * the old page simply hid every section it had nothing for, which is why it read
 * as empty rather than as quiet.
 */
export interface EngagementFacts {
  /** value of deals that are signed and running */
  inDelivery: number
  deliveryCount: number
  /** value of deals still open, i.e. not won, lost or closed */
  pipeline: number
  pipelineCount: number
  /** value of everything ever won on this client */
  won: number
  /** soonest dated group across every board, or undefined */
  next: GroupRecord | undefined
  /** unfinished items across every board */
  openItems: number
  boardCount: number
  /** `occurredAt` of the newest entry anywhere on this client, or '' */
  lastContact: string
}

export function engagementFacts(state: State, config: Config, orgId: string): EngagementFacts {
  const deals = dealsForOrg(state, orgId)
  const kindOf = (d: EntityRecord) =>
    config.stages.find((st) => st.key === stageOf(d, config.stages))?.kind
  const value = (d: EntityRecord) => Number(d.fields.value) || 0

  const inDeliveryDeals = deals.filter((d) => kindOf(d) === 'delivery')
  const openDeals = deals.filter((d) => kindOf(d) === 'open')

  const works = projectsForOrg(state, orgId)
  const boards = works.filter((w) => isBoard(state, w.id))
  const openItems = works.reduce(
    (n, w) => n + itemsForWork(state, w.id).filter((i) => i.status !== 'done').length,
    0,
  )

  return {
    inDelivery: inDeliveryDeals.reduce((n, d) => n + value(d), 0),
    deliveryCount: inDeliveryDeals.length,
    pipeline: openDeals.reduce((n, d) => n + value(d), 0),
    pipelineCount: openDeals.length,
    // `won` and `delivery` are separate stage kinds, and both are money in the bank.
    won: deals
      .filter((d) => kindOf(d) === 'won' || kindOf(d) === 'delivery' || kindOf(d) === 'closed')
      .reduce((n, d) => n + value(d), 0),
    next: milestonesForWorks(
      state,
      works.map((w) => w.id),
    )[0],
    openItems,
    boardCount: boards.length,
    lastContact: lastContact(state, orgId),
  }
}

// ── selectors: support edges ─────────────────────────────────────────────────

export function supportFor(state: State, dealId: string): SupportEdge[] {
  return Object.values(state.support).filter((s) => s.deal === dealId)
}

/** Deals this work is attached to. */
/** Whose work this is: the orgs on the deals paying for it. Usually one, and a
 *  joint project genuinely has several — so a caller shows all of them rather
 *  than picking. */
export function orgsForProject(state: State, projectId: string): EntityRecord[] {
  const seen = new Map<string, EntityRecord>()
  // A direct link first: internal work and anything that predates a contract has no
  // deal to reach a client through, and the client is the spine — every project has
  // to be able to say whose it is without one.
  for (const org of linkedOfKind(state, projectId, 'org')) seen.set(org.id, org)
  for (const deal of dealsForProject(state, projectId))
    for (const org of orgsForDeal(state, deal.id)) seen.set(org.id, org)
  return [...seen.values()]
}

export function dealsForProject(state: State, projectId: string): EntityRecord[] {
  const ids = new Set(
    Object.values(state.support)
      .filter((s) => s.project === projectId)
      .map((s) => s.deal),
  )
  return [...ids]
    .map((id) => state.entities[id])
    .filter((d): d is EntityRecord => !!d && d.kind === 'deal' && !d.archived)
    .sort(newestFirst)
}

/** The work associated with a deal. */
export function projectsForDeal(state: State, dealId: string): EntityRecord[] {
  return supportFor(state, dealId)
    .map((s) => state.entities[s.project])
    .filter((p): p is EntityRecord => !!p && p.kind === 'project' && !p.archived)
    .sort(newestFirst)
}

/** Which stage bucket a deal sits in, tolerating a stage key that no longer
 *  exists in config (renamed or removed): fall back to the first open stage. */
export function stageOf(deal: EntityRecord, stages: { key: string; kind: string }[]): string {
  const s = fieldStr(deal, 'stage')
  if (stages.some((st) => st.key === s)) return s
  return stages.find((st) => st.kind === 'open')?.key ?? stages[0]?.key ?? 'idea'
}

/**
 * Is this work currently earning? Read off the deals attached to it rather than a flag
 * someone has to remember to change:
 *
 *   delivery · attached to a signed deal (won or in delivery) — it's live
 *   pipeline · attached only to open deals — it might become live
 *   done     · an attached deal was delivered — it happened, it's over
 *   lost     · every attached deal was lost. Built, shown, didn't land.
 *   none     · nothing attached. Most demos live here, and that's fine.
 *
 * Ordered by liveness, so work on both an open and a signed deal reads as live.
 */
export function workStatusOf(state: State, projectId: string, config: Config): WorkStatus {
  const deals = dealsForProject(state, projectId)
  if (deals.length === 0) return 'none'
  const kinds = deals.map(
    (d) => config.stages.find((s) => s.key === stageOf(d, config.stages))?.kind,
  )
  if (kinds.some(isCommitted)) return 'delivery'
  if (kinds.some((k) => k === 'open')) return 'pipeline'
  if (kinds.some((k) => k === 'closed')) return 'done'
  if (kinds.some((k) => k === 'lost')) return 'lost'
  return 'none'
}

// ── selectors: the renewal chain ─────────────────────────────────────────────
//
// A multi-year relationship is a chain of deals rather than a container above them:
// FY26 `renews` FY25 `renews` FY24. That gives the grouping a separate record would
// have given — a head that carries the name, and a lifetime value — without a table,
// and it's honest about the fact that each renewal is its own contract with its own
// term, value and deliverables.

/** Walk back to the first deal in the chain. */
export function chainHead(state: State, dealId: string): string {
  const seen = new Set<string>([dealId])
  let cur = dealId
  for (;;) {
    const prev = fieldStr(state.entities[cur], 'renews')
    // A cycle would only come from hand-edited data, but it would hang the walk.
    if (!prev || seen.has(prev) || state.entities[prev]?.kind !== 'deal') return cur
    seen.add(prev)
    cur = prev
  }
}

/** The whole chain a deal belongs to, oldest first. */
export function renewalChain(state: State, dealId: string): EntityRecord[] {
  const head = chainHead(state, dealId)
  const out: EntityRecord[] = []
  const seen = new Set<string>()
  let cur: string | undefined = head
  while (cur && !seen.has(cur)) {
    seen.add(cur)
    const rec = state.entities[cur]
    if (!rec) break
    out.push(rec)
    cur = Object.values(state.entities).find(
      (d) => d.kind === 'deal' && !d.archived && fieldStr(d, 'renews') === cur,
    )?.id
  }
  return out
}

/** Money booked across the whole relationship, every renewal included. */
export function chainValue(state: State, dealId: string, config: Config): number {
  return renewalChain(state, dealId)
    .filter((d) => isBooked(config.stages.find((s) => s.key === stageOf(d, config.stages))?.kind))
    .reduce((sum, d) => sum + (Number(d.fields.value) || 0), 0)
}

// ── selectors: mentions, backlinks, lineage ──────────────────────────────────

/** Every id this record's own rich fields point at. */
export function outboundMentions(state: State, id: string): string[] {
  const rec = state.entities[id]
  if (!rec) return []
  const ids = new Set<string>()
  for (const key of mentionFields(rec.kind)) {
    for (const m of mentionedIds(fieldStr(rec, key))) if (m !== id) ids.add(m)
  }
  return [...ids]
}

export interface Backlink {
  /** the record doing the mentioning */
  from: string
  /** how: a field key on an entity, or 'entry' */
  via: string
  /** for via === 'entry', the entry id */
  entryId?: string
}

/**
 * Who points at me. Derived, never stored — this is what makes @mentions the
 * connective tissue rather than a second copy of the relationship model.
 */
export function backlinks(state: State, id: string): Backlink[] {
  const out: Backlink[] = []
  for (const rec of Object.values(state.entities)) {
    if (rec.id === id || rec.archived) continue
    for (const key of mentionFields(rec.kind)) {
      if (mentionedIds(fieldStr(rec, key)).includes(id)) out.push({ from: rec.id, via: key })
    }
  }
  for (const e of Object.values(state.entries)) {
    if (e.archived || e.subject === id) continue
    if (mentionedIds(e.body).includes(id))
      out.push({ from: e.subject, via: 'entry', entryId: e.id })
  }
  return out
}

/**
 * What this record builds on: the work its own description mentions.
 *
 * One implementation, shared with the ecosystem view, because there was no version
 * of "two ways to read the same prose" that stayed in agreement. Covers artifacts as
 * well as projects — an artifact that reuses a method is as much a descendant as a
 * project is.
 *
 * Deliberately ONE-directional: it answers "what did this come out of". The other
 * direction is this one reversed (see `informs`), which is why naming downstream work
 * in your own description would invert an edge — the field's placeholder says
 * "@mention the work it builds on" for exactly that reason.
 */
export function buildsOn(state: State, id: string): string[] {
  return connectionsFor(state, id)
    .filter((c) => c.to === id)
    .map((c) => c.from)
}

/** The reverse: everything whose description names this one. */
export function informs(state: State, id: string): string[] {
  return connectionsFor(state, id)
    .filter((c) => c.from === id)
    .map((c) => c.to)
}

/** How load-bearing a piece of work is: how much else builds on it. */
export function reuseScore(state: State, projectId: string): number {
  return informs(state, projectId).length
}

// ── selectors: tags ──────────────────────────────────────────────────────────

export function allTags(state: State): { label: string; count: number }[] {
  const counts = new Map<string, number>()
  for (const e of Object.values(state.entities)) {
    if (e.archived) continue
    for (const t of e.tags) counts.set(t, (counts.get(t) ?? 0) + 1)
  }
  return [...counts.entries()]
    .map(([label, count]) => ({ label, count }))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label))
}

export function entitiesWithTag(state: State, label: string): EntityRecord[] {
  return Object.values(state.entities)
    .filter((e) => !e.archived && e.tags.includes(label))
    .sort(newestFirst)
}
